import { sleep } from "./timing";
import { getAssetUrl } from "./url";

export interface FetchRetryOptions {
  delay?: number;
  retries?: number;
  timeout?: number;
}

/**
 * Enumeration of network error reasons.
 */
export const NetworkErrorReason = {
  Timeout: "TIMEOUT",
  NetworkFailure: "NETWORK_FAILURE",
  Aborted: "ABORTED",
} as const;

export type NetworkErrorReason =
  (typeof NetworkErrorReason)[keyof typeof NetworkErrorReason];

/**
 * Error class for network-level failures.
 */
export class BpNetworkError<
  R extends string = NetworkErrorReason,
> extends Error {
  constructor(
    message: string,
    public readonly reason: R,
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "BpNetworkError";
  }
}

/**
 * Fetches a remote asset.
 */
export async function fetchAsset(
  path: string,
  signal?: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = getAssetUrl(path);

    fetch(url, { signal })
      .then(async (r) => {
        const text = await r.text();

        if (!r.ok) reject(text);
        resolve(text);
      })
      .catch((e) => reject(e));
  });
}

/**
 * Fetch, but retry (after a delay) if the request fails.
 */
export async function fetchWithRetry(
  url: RequestInfo | URL,
  init?: RequestInit,
  retryOptions: FetchRetryOptions = {},
): Promise<Response> {
  const { retries = 2, delay = 1000, timeout = 30000 } = retryOptions;

  const retry = async () => {
    await sleep(delay);

    return fetchWithRetry(url, init, {
      retries: retries - 1,
      delay: delay * 2,
      timeout,
    });
  };

  // Create AbortController for timeouts
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

  // Merge caller's signal with timeout signal if provided.
  // This will cause the timeout to be aborted if the caller
  // aborts the request.
  if (init?.signal) {
    init.signal.addEventListener("abort", () => timeoutController.abort());
  }

  try {
    const response = await fetch(url, {
      ...init,
      signal: timeoutController.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok && retries > 0 && shouldRetryFetch(null, response)) {
      return retry();
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    // Check if this is an abort error (either timeout or
    // external abort)
    if (error instanceof Error && error.name === "AbortError") {
      // Don't retry if the request was aborted by the caller
      if (init?.signal?.aborted) {
        throw new BpNetworkError(
          `Request aborted: ${url}`,
          NetworkErrorReason.Aborted,
        );
      }

      // Retry if the request timed out
      if (retries > 0) {
        return retry();
      }

      // Otherwise, throw a timeout error
      throw new BpNetworkError(
        `Request timeout after ${timeout}ms: ${url}`,
        NetworkErrorReason.Timeout,
      );
    }

    // Network or other errors
    if (retries > 0 && shouldRetryFetch(error)) {
      return retry();
    }

    // Wrap unknown errors in BpNetworkError
    if (error instanceof Error) {
      throw new BpNetworkError(
        error.message,
        NetworkErrorReason.NetworkFailure,
        { url: url.toString() },
      );
    }

    throw error;
  }
}

/**
 * Determines if a request should be retried based on the
 * error type and response.
 */
export function shouldRetryFetch(error: unknown, response?: Response): boolean {
  // Don't retry client errors (4xx) - these indicate invalid requests
  if (response && response.status >= 400 && response.status < 500) {
    return false;
  }

  // Retry server errors (5xx) - these may be temporary
  if (response && response.status >= 500) {
    return true;
  }

  // Retry on network errors and timeouts
  if (error instanceof Error) {
    // AbortError indicates timeout or cancellation
    if (error.name === "AbortError") {
      return true;
    }

    // TypeError typically indicates network failures
    if (error.name === "TypeError") {
      return true;
    }
  }

  // Retry BpNetworkError based on reason
  if (error instanceof BpNetworkError) {
    return (
      error.reason === NetworkErrorReason.Timeout ||
      error.reason === NetworkErrorReason.NetworkFailure
    );
  }

  // Default to retrying unknown errors
  return true;
}
